import { Type } from '@angular/core';
import { Observable, combineLatest, of, EMPTY } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { ButtonConfig, ModalService, ModalType } from '@ui-lib';
import { TranslocoService } from '@jsverse/transloco';
import { GLOBAL_STYLES } from '@common/utils/global-styles';

export interface ErrorModalOptions {
  testId: string;
  /** Omit along with titleKey for a description-only modal (no title). */
  titleTestId?: string;
  messageTestId: string;
  /** i18n key for the modal title — translated via selectTranslate(). Omit for a description-only modal. */
  titleKey?: string;
  /** i18n key for the message — translated when provided. */
  messageKey?: string;
  /** Raw backend message used directly (e.g. from error.error?.message). */
  backendMessage?: string;
  type?: ModalType;
  icon?: Type<unknown>;
  buttons: ButtonConfig[];
}

/**
 * Resolves i18n keys via selectTranslate() (awaits async load()), opens a
 * styled error modal using the app-wide GLOBAL_STYLES, then returns EMPTY to
 * suppress the error from propagating further down the stream.
 */
export function openErrorModal(
  transloco: TranslocoService,
  modal: ModalService,
  options: ErrorModalOptions
): Observable<never> {
  const title$ = options.titleKey
    ? transloco.selectTranslate(options.titleKey)
    : of(undefined);
  const message$ = options.messageKey
    ? transloco.selectTranslate(options.messageKey)
    : of(options.backendMessage ?? '');

  return combineLatest([title$, message$]).pipe(
    take(1),
    tap(([title, message]) =>
      modal.open({
        testId: options.testId,
        titleTestId: options.titleTestId,
        messageTestId: options.messageTestId,
        type: options.type,
        icon: options.icon as never,
        title,
        titleClass: GLOBAL_STYLES.modal.titleClass,
        message,
        messageClass: GLOBAL_STYLES.modal.messageClass,
        modalClass: GLOBAL_STYLES.modal.modalClass,
        mediaClass: GLOBAL_STYLES.modal.mediaClass,
        contentClass: GLOBAL_STYLES.modal.contentClass,
        buttonsClass: GLOBAL_STYLES.modal.buttonsClass,
        buttons: options.buttons,
      })
    ),
    switchMap(() => EMPTY)
  );
}
